/// Pinia auth store — JWT tokens + user profile state + login/logout actions.
/// Phase 2 Step 2.2: login() + logout() + fetchMe() wired qua axios-client.

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import axios from 'axios'

import { apiErrorMessage, apiErrorKey } from '@/core/api/api-error'
import { i18n, persistLocale, setI18nLocale } from '@/core/i18n'
import { WEB_PUSH_ENABLED } from '@/shared/config/features'

import type {
  Locale,
  LoginRequest,
  LoginResponse,
  MeResponse,
  UserProfile,
} from '@/types/api'

const ACCESS_TOKEN_KEY = 'tts.access_token'
// Legacy key: refresh token from BEFORE the R2 migration (now in the HttpOnly
// cookie). Read once to bootstrap the cookie for old users (see axios-client refresh).
const LEGACY_REFRESH_TOKEN_KEY = 'tts.refresh_token'
// Persisted "remember me" choice — decides access-token storage (local vs session)
// and is read on token refresh (which doesn't carry the original choice).
const REMEMBER_KEY = 'tts.remember'

const hasSession = (): boolean => typeof sessionStorage !== 'undefined'
const hasLocal = (): boolean => typeof localStorage !== 'undefined'

/** Read access token from localStorage (persistent) or sessionStorage (session-only). */
function readAccessToken(): string | null {
  return (
    (hasLocal() ? localStorage.getItem(ACCESS_TOKEN_KEY) : null) ??
    (hasSession() ? sessionStorage.getItem(ACCESS_TOKEN_KEY) : null)
  )
}

/** Resolve the remembered choice (default true when absent — legacy/back-compat). */
function readRemember(): boolean {
  return hasLocal() ? localStorage.getItem(REMEMBER_KEY) !== '0' : true
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

// Bare client for login/logout/me. withCredentials: send/receive the HttpOnly
// refresh cookie (R2). No refresh interceptor (avoids a circular loop).
const bareClient = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

export const useAuthStore = defineStore('auth', () => {
  // Access token: persistent (localStorage) when "remember me" is on, else
  // session-only (sessionStorage, cleared on browser close). Read from either.
  const accessToken = ref<string | null>(readAccessToken())
  // Web no longer keeps the refresh token in JS/localStorage — it lives in the
  // HttpOnly cookie (R2, prevents XSS token theft). This ref only holds a legacy
  // value (for users logged in before R2) to bootstrap the cookie once; null after.
  const refreshToken = ref<string | null>(
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY)
      : null,
  )
  const profile = ref<MeResponse | null>(null)
  const locale = ref<'ja' | 'en'>('ja')
  const loginError = ref<string | null>(null)
  // IP the server determined for a blocked login (IP_NOT_ALLOWED) — shown on the
  // login page so the user can copy it and ask an admin to whitelist it (FR-019).
  const loginBlockedIp = ref<string | null>(null)
  const isLoggingIn = ref(false)

  const isAuthenticated = computed(() => Boolean(accessToken.value))
  const user = computed<UserProfile | null>(() => profile.value?.user ?? null)

  /**
   * Super Admin TTS: tenantId = null, hoặc role super_admin / permission '*'.
   * Ưu tiên profile (đầy đủ); fallback decode JWT claim để vẫn đúng NGAY sau khi
   * reload trang (profile in-memory đã mất, chưa kịp fetchMe) — tránh ẩn nhầm tab
   * "Quản lý công ty" + tránh guard route đá super_admin ra ngoài.
   */
  const isSuperAdmin = computed(() => {
    if (profile.value) {
      return (
        user.value?.tenantId === null ||
        profile.value.roles.some((r) => r.role === 'super_admin') ||
        profile.value.permissions.includes('*')
      )
    }
    const claims = decodeJwtClaims(accessToken.value)
    if (!claims) return false
    return (
      claims.tenant_id == null ||
      (claims.roles?.includes('super_admin') ?? false) ||
      (claims.permissions?.includes('*') ?? false)
    )
  })

  /**
   * Does the caller hold a permission? Mirrors the backend check, wildcard included.
   *
   * Use this instead of a role proxy when the UI gates on the same permission the server
   * gates on. `isCorpAdmin` is deliberately false for a Super Admin, but the server lets
   * them through on `*` — so a role proxy locks out someone the API would accept.
   * Profile first, JWT claims as the fallback (same order as the flags below).
   */
  function hasPermission(permission: string): boolean {
    const perms = profile.value?.permissions ?? decodeJwtClaims(accessToken.value)?.permissions
    if (!perms) return false
    return perms.includes('*') || perms.includes(permission)
  }

  /**
   * Corp Admin (tenant admin): role corp_admin OR the tenant-corp-admin:write
   * permission. Never true for a Super Admin (they manage Corp Admins via the
   * cross-tenant screen instead). Mirrors isSuperAdmin's profile-then-JWT fallback
   * so it stays correct right after a page reload (before fetchMe completes).
   */
  const isCorpAdmin = computed(() => {
    if (isSuperAdmin.value) return false
    if (profile.value) {
      return (
        profile.value.roles.some((r) => r.role === 'corp_admin') ||
        profile.value.permissions.includes('tenant-corp-admin:write')
      )
    }
    const claims = decodeJwtClaims(accessToken.value)
    if (!claims) return false
    return (
      (claims.roles?.includes('corp_admin') ?? false) ||
      (claims.permissions?.includes('tenant-corp-admin:write') ?? false)
    )
  })

  /**
   * Dept Manager (trưởng phòng): role dept_manager. Cùng fallback profile→JWT như
   * isCorpAdmin. Dùng cho AST-detail FR-017 (gắn/gỡ thẻ + gán/gỡ quản lý mở cho
   * cả Corp Admin lẫn Trưởng phòng).
   */
  const isDeptManager = computed(() => {
    if (isSuperAdmin.value) return false
    if (profile.value) {
      return profile.value.roles.some((r) => r.role === 'dept_manager')
    }
    const claims = decodeJwtClaims(accessToken.value)
    if (!claims) return false
    return claims.roles?.includes('dept_manager') ?? false
  })

  /**
   * Store the access token (in-memory + persisted so it survives reload). The
   * refresh token is no longer kept in JS (R2): the server sets an HttpOnly cookie.
   *
   * @param refresh kept only for signature compat (web cookie-mode passes undefined).
   * @param persistent "remember me". true → localStorage (survives restart);
   *   false → sessionStorage (cleared on browser close). When omitted (e.g. token
   *   refresh, which doesn't carry the choice), fall back to the remembered flag.
   */
  function setTokens(
    access: string,
    refresh?: string | null,
    persistent?: boolean,
  ) {
    accessToken.value = access
    const remember = persistent ?? readRemember()
    if (hasLocal() && persistent !== undefined) {
      localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0')
    }
    // Write to the chosen storage; clear the other to avoid a stale duplicate.
    if (remember) {
      if (hasLocal()) localStorage.setItem(ACCESS_TOKEN_KEY, access)
      if (hasSession()) sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    } else {
      if (hasSession()) sessionStorage.setItem(ACCESS_TOKEN_KEY, access)
      if (hasLocal()) localStorage.removeItem(ACCESS_TOKEN_KEY)
    }
    // refresh only updates the in-memory ref (not persisted). Web cookie-mode: skip.
    if (refresh) refreshToken.value = refresh
  }

  /**
   * Apply a locale everywhere it matters: store ref (→ Accept-Language header),
   * vue-i18n (→ UI text), localStorage (→ survives reload). Fixes the bug where
   * switching language only changed vue-i18n in memory and was lost on reload.
   */
  function applyLocale(next: Locale) {
    locale.value = next
    setI18nLocale(next)
    persistLocale(next)
  }

  function setProfile(me: MeResponse) {
    profile.value = me
    // Backend users.locale is the source of truth once signed in.
    applyLocale(me.user.locale)
  }

  function clear() {
    accessToken.value = null
    refreshToken.value = null
    profile.value = null
    loginError.value = null
    loginBlockedIp.value = null
    if (hasLocal()) {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      // Clean up the legacy refresh token if still present (now an HttpOnly cookie).
      localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY)
      localStorage.removeItem(REMEMBER_KEY)
    }
    if (hasSession()) sessionStorage.removeItem(ACCESS_TOKEN_KEY)
  }

  /**
   * Login with email + password.
   * Success: store tokens + profile, return true.
   * Failure: set loginError, return false.
   */
  async function login(payload: LoginRequest): Promise<boolean> {
    isLoggingIn.value = true
    loginError.value = null
    loginBlockedIp.value = null
    try {
      const { data } = await bareClient.post<LoginResponse>(
        '/auth/login',
        payload,
      )
      // rememberMe decides access-token persistence (default true).
      setTokens(data.accessToken, data.refreshToken, payload.rememberMe ?? true)
      // Profile from login response — minimum needed for UI.
      profile.value = {
        user: data.user,
        tenant: data.tenant,
        roles: [], // refetched via fetchMe()
        permissions: [],
        consented: false,
        pushNotificationEnabled: false,
      }
      applyLocale(data.user.locale)
      // Roles chỉ về qua /auth/me → await để chạy được web-admin gate.
      await fetchMe()
      // Web admin CHỈ dành cho Super Admin + Corp Admin (spec.md §58-59). End-user
      // (dept_manager / employee) dùng app mobile → từ chối login web, xóa token vừa
      // lưu. Báo rõ lý do thiếu quyền truy cập (không phải sai email/mật khẩu).
      if (!isSuperAdmin.value && !isCorpAdmin.value) {
        clear()
        loginError.value = i18n.global.t('errNotAdminAccess')
        return false
      }
      // Re-register this browser for push if it already granted permission
      // (silent — no prompt). Dynamic import avoids a static auth↔fcm cycle.
      if (WEB_PUSH_ENABLED) {
        void import('@/core/fcm/push-manager').then((m) => m.syncPush())
      }
      return true
    } catch (err) {
      // Map by `code` (localized ja/en) via apiErrorMessage → show the SPECIFIC
      // reason: account locked / deactivated / not-activated / IP_NOT_ALLOWED, etc.
      // Invalid credentials (AUTH_INVALID_CREDENTIALS) stays generic in i18n
      // ("incorrect email or password") → anti-enumeration, never reveals which
      // field was wrong. Never use the backend `title` (developer-English).
      loginError.value = apiErrorMessage(err)
      // For an IP/device block the server returns the determined identifier in
      // `params.identifier` (RFC9457 body) → surface it so the user can copy it.
      loginBlockedIp.value = blockIdentifier(err)
      return false
    } finally {
      isLoggingIn.value = false
    }
  }

  /**
   * Handle an access-whitelist block (IP_NOT_ALLOWED / DEVICE_NOT_WHITELISTED)
   * that hit a request while the user was already signed in (the company turned
   * blocking on, or the session moved to a non-whitelisted network/device).
   * Drops the session and surfaces the SAME blocked banner the login page shows
   * (reason + identifier to copy), so the user is bounced back to login with a
   * clear explanation instead of a silent error. Called by the axios interceptor.
   */
  function flagAccessBlocked(err: unknown) {
    clear() // wipes tokens + loginError + loginBlockedIp first…
    loginError.value = apiErrorMessage(err) // …then restore the localized reason
    loginBlockedIp.value = blockIdentifier(err)
  }

  /**
   * Session refused for good by /auth/refresh (cookie gone, token revoked, CSRF).
   * Same shape as flagAccessBlocked: drop the session, then leave the localized reason
   * (`AUTH_REFRESH_INVALID` / `CSRF_INVALID` → "session invalid, sign in again") for
   * the login page to show — the bounce must not be silent. Called by the axios
   * interceptor.
   */
  function flagSessionExpired(err: unknown) {
    clear()
    loginError.value = apiErrorMessage(err)
  }

  /**
   * Activate an account via the activation token (link in the invite email).
   * Backend returns LoginResponse → auto-login (set tokens). Returns {ok, error?}.
   */
  async function activate(payload: {
    token: string
    newPassword: string
    consentPolicyVersion: string
    consentAccepted: boolean
  }): Promise<{ ok: boolean; error?: string }> {
    try {
      const { data } = await bareClient.post<LoginResponse>('/auth/activate', payload)
      // Activation = first auto-login → persist session ("remember me" default).
      setTokens(data.accessToken, data.refreshToken, true)
      profile.value = {
        user: data.user,
        tenant: data.tenant,
        roles: [],
        permissions: [],
        consented: true,
        pushNotificationEnabled: false, // refetched via fetchMe()
      }
      applyLocale(data.user.locale)

      await fetchMe()
      return { ok: true }
    } catch (err) {
      return { ok: false, error: extractErr(err) }
    }
  }

  /**
   * Verify an activation link on page load (GET /auth/activate) WITHOUT consuming
   * it — so a dead link (used/expired/already active) shows its reason up front
   * instead of only after the user submits a password. On failure also returns the
   * backend `code` and, for ACCOUNT_ALREADY_ACTIVE, `loginScope` ('web' | 'mobile')
   * so the page can send an already-active user to the right login.
   */
  async function verifyActivationToken(
    token: string,
  ): Promise<{ ok: boolean; error?: string; code?: string; loginScope?: 'web' | 'mobile' }> {
    try {
      await bareClient.get('/auth/activate', { params: { token } })
      return { ok: true }
    } catch (err) {
      const body = axios.isAxiosError(err)
        ? (err.response?.data as { code?: string; params?: { loginScope?: 'web' | 'mobile' } })
        : undefined
      return {
        ok: false,
        error: extractErr(err),
        code: body?.code,
        loginScope: body?.params?.loginScope,
      }
    }
  }

  /**
   * Kiểm link reset lúc mở trang (GET /auth/reset-password) — KHÔNG consume token —
   * để link chết (đã dùng/hết hạn) hiện lý do ngay thay vì form nhập mật khẩu.
   * Trả `errorKey` (i18n key) để thông báo đổi theo ngôn ngữ.
   */
  async function verifyResetToken(
    token: string,
  ): Promise<{ ok: boolean; errorKey?: string }> {
    try {
      await bareClient.get('/auth/reset-password', { params: { token } })
      return { ok: true }
    } catch (err) {
      return { ok: false, errorKey: extractErrKey(err) }
    }
  }

  /** Reset password via the reset token (link in the forgot-password email). */
  async function resetPassword(payload: {
    token: string
    newPassword: string
  }): Promise<{ ok: boolean; error?: string }> {
    try {
      await bareClient.post('/auth/reset-password', payload)
      return { ok: true }
    } catch (err) {
      return { ok: false, error: extractErr(err) }
    }
  }

  /**
   * Request a password reset email. Anti-enumeration: backend always returns 200,
   * so this resolves ok regardless of whether the email exists. Only a transport
   * error surfaces as a failure.
   */
  async function forgotPassword(
    email: string,
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      await bareClient.post('/auth/forgot-password', { email })
      return { ok: true }
    } catch (err) {
      return { ok: false, error: extractErr(err) }
    }
  }

  /**
   * Change the password for the signed-in user (current + new password).
   * Backend verifies the current password and revokes ALL refresh tokens on success
   * (force re-login). The caller should log out + redirect to /login afterwards.
   * Confirm ("nhập lại pass mới") is validated client-side, not sent to the server.
   */
  async function changePassword(payload: {
    currentPassword: string
    newPassword: string
  }): Promise<{ ok: boolean; error?: string }> {
    if (!accessToken.value) return { ok: false, error: extractErr(null) }
    try {
      await bareClient.post('/auth/me/change-password', payload, {
        headers: { Authorization: `Bearer ${accessToken.value}` },
      })
      return { ok: true }
    } catch (err) {
      return { ok: false, error: extractErr(err) }
    }
  }

  /**
   * Change the signed-in user's display language. Persists to the backend
   * (PATCH /auth/me → users.locale, also used for emails/push) and only then
   * applies it locally (i18n + Accept-Language + localStorage).
   */
  async function updateLocale(next: Locale): Promise<{ ok: boolean; errorKey?: string }> {
    if (locale.value === next) return { ok: true }
    if (!accessToken.value) return { ok: false, errorKey: extractErrKey(null) }
    try {
      await bareClient.patch(
        '/auth/me',
        { locale: next },
        { headers: { Authorization: `Bearer ${accessToken.value}` } },
      )
      applyLocale(next)
      // Replace (not mutate) — setProfile may hold a caller-owned object.
      if (profile.value) {
        profile.value = {
          ...profile.value,
          user: { ...profile.value.user, locale: next },
        }
      }
      return { ok: true }
    } catch (err) {
      return { ok: false, errorKey: extractErrKey(err) }
    }
  }

  /**
   * Toggle the signed-in user's push opt-in. Persists to the backend
   * (PATCH /auth/me → users.push_notification_enabled, used to filter FCM
   * recipients) and mirrors it into the in-memory profile. The browser-side
   * token (register/unregister) is handled by the My Page toggle via push-manager.
   */
  async function updatePushNotificationEnabled(
    next: boolean,
  ): Promise<{ ok: boolean; errorKey?: string }> {
    if (!accessToken.value) return { ok: false, errorKey: extractErrKey(null) }
    try {
      await bareClient.patch(
        '/auth/me',
        { pushNotificationEnabled: next },
        { headers: { Authorization: `Bearer ${accessToken.value}` } },
      )
      if (profile.value) {
        profile.value = { ...profile.value, pushNotificationEnabled: next }
      }
      return { ok: true }
    } catch (err) {
      return { ok: false, errorKey: extractErrKey(err) }
    }
  }

  /**
   * Change (file) or remove (null) the signed-in user's profile photo.
   * Mirrors the category-icon upload flow: presigned PUT straight to S3 with
   * bare axios (no Authorization header — the URL carries its own signature),
   * then PATCH /auth/me with the S3 key. The PATCH response is the updated
   * user (incl. a fresh presigned avatarUrl) → replaces profile.user.
   */
  async function updateAvatar(
    file: File | null,
  ): Promise<{ ok: boolean; errorKey?: string }> {
    if (!accessToken.value) return { ok: false, errorKey: extractErrKey(null) }
    const auth = { headers: { Authorization: `Bearer ${accessToken.value}` } }
    try {
      let key: string | null = null
      if (file) {
        const { data } = await bareClient.post<{ key: string; uploadUrl: string }>(
          '/auth/me/avatar-upload-url',
          { contentType: file.type },
          auth,
        )
        await axios.put(data.uploadUrl, file, {
          headers: { 'Content-Type': file.type },
        })
        key = data.key
      }
      const { data: updated } = await bareClient.patch<UserProfile>(
        '/auth/me',
        { avatar: key },
        auth,
      )
      // Replace (not mutate) — same rule as updateLocale.
      if (profile.value) {
        profile.value = { ...profile.value, user: updated }
      }
      return { ok: true }
    } catch (err) {
      return { ok: false, errorKey: extractErrKey(err) }
    }
  }

  /**
   * Refetch the user profile via GET /auth/me — called after login or whenever
   * roles/permissions/consented are needed. Uses bareClient + manual Authorization
   * to avoid a circular refresh-interceptor loop.
   */
  async function fetchMe(): Promise<void> {
    if (!accessToken.value) return
    try {
      const { data } = await bareClient.get<MeResponse>('/auth/me', {
        headers: { Authorization: `Bearer ${accessToken.value}` },
      })
      setProfile(data)
    } catch {
      // silent — the interceptor handles 401 on other calls
    }
  }

  /**
   * Logout — invalidate the refresh token on the backend, clear local state.
   */
  async function logout(): Promise<void> {
    // Call logout when an access token exists (Bearer). The refresh token travels
    // via the HttpOnly cookie (withCredentials) — independent of refreshToken.value
    // (null in web cookie-mode). Still send body refreshToken if a legacy one remains.
    if (accessToken.value) {
      // Remove this browser's push token BEFORE clearing (the DELETE needs the
      // Bearer). Best-effort — never block logout. Dynamic import avoids a cycle.
      // Skipped while push is off: disablePush() would otherwise ask for a token,
      // which registers the service worker we are trying to keep out of the browser.
      if (WEB_PUSH_ENABLED) {
        try {
          await import('@/core/fcm/push-manager').then((m) => m.disablePush())
        } catch {
          // ignore
        }
      }
      try {
        await bareClient.post(
          '/auth/logout',
          refreshToken.value ? { refreshToken: refreshToken.value } : {},
          { headers: { Authorization: `Bearer ${accessToken.value}` } },
        )
      } catch {
        // ignore — clear local anyway
      }
    }
    clear()
  }

  return {
    // state
    accessToken,
    refreshToken,
    profile,
    locale,
    loginError,
    loginBlockedIp,
    isLoggingIn,
    // getters
    isAuthenticated,
    user,
    isSuperAdmin,
    isCorpAdmin,
    hasPermission,
    isDeptManager,
    // actions
    setTokens,
    setProfile,
    clear,
    login,
    flagAccessBlocked,
    flagSessionExpired,
    logout,
    fetchMe,
    activate,
    verifyActivationToken,
    verifyResetToken,
    resetPassword,
    forgotPassword,
    changePassword,
    updateLocale,
    updatePushNotificationEnabled,
    updateAvatar,
  }
})

/** Message lỗi ĐÃ LOCALIZE cho activate/reset (không dùng text backend tiếng Việt). */
function extractErr(err: unknown): string {
  return apiErrorMessage(err)
}

/**
 * i18n KEY của lỗi (không resolve) — caller (MyPage) lưu key rồi render `t(key)` để
 * thông báo lỗi đổi theo ngôn ngữ khi user chuyển JA↔EN. `null` → key mặc định.
 */
function extractErrKey(err: unknown): string {
  return apiErrorKey(err)
}

/**
 * Extract the server-determined identifier (IP or device id) from an access
 * whitelist block (IP_NOT_ALLOWED / DEVICE_NOT_WHITELISTED, RFC9457
 * `params.identifier`). Returns null for any other error — so it doubles as the
 * "is this a whitelist block?" check for the login banner.
 */
function blockIdentifier(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null
  const body = err.response?.data as
    | { code?: string; params?: { identifier?: string } }
    | undefined
  if (
    (body?.code === 'IP_NOT_ALLOWED' ||
      body?.code === 'DEVICE_NOT_WHITELISTED') &&
    body.params?.identifier
  ) {
    return body.params.identifier
  }
  return null
}

interface JwtClaims {
  tenant_id?: string | null
  roles?: string[]
  permissions?: string[]
}

/** Decode payload JWT (chỉ đọc claim, KHÔNG verify chữ ký — chỉ dùng cho UI hint). */
function decodeJwtClaims(token: string | null): JwtClaims | null {
  if (!token) return null
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    return JSON.parse(json) as JwtClaims
  } catch {
    return null
  }
}
